import { Controller } from "stimulus";
import Rails from "@rails/ujs";

export default class extends Controller {
  static targets = [
    'iconExpanded',
    'iconCollapsed',
    'mobileIcon',
    'logo',
    'menuItem',
    'topNav',
    'content',
    'userMenu',
    'userAvatar',
    'submenuContent',
    'submenuIcon',
    'logoIcon'
  ];

  connect() {
    this._boundCollapseOnOutsideClick = this._collapseOnOutsideClick.bind(this);
    this._boundPrepareForCache = this._prepareForCache.bind(this);
    document.addEventListener('click', this._boundCollapseOnOutsideClick);
    document.addEventListener('turbolinks:before-cache', this._boundPrepareForCache);

    // Remember which group the server rendered as active for this URL,
    // so the Turbolinks cache snapshot matches the server state.
    this._serverActiveGroup = this.submenuContentTargets.find(
      el => !el.classList.contains('collapsed')
    ) || null;

    this._positionUserMenuContent();
  }

  disconnect() {
    document.removeEventListener('click', this._boundCollapseOnOutsideClick);
    document.removeEventListener('turbolinks:before-cache', this._boundPrepareForCache);
  }

  toggle() {
    this._toggleSidebarClasses();
    this._toggleTopNavClasses();
    this._toggleLogoClasses();
    this._toggleIconClasses();
    this._hideItemText();
    this._toggleAvatarPosition();
    this._toggleUserMenu();
    this._collapseMenuGroupsIfSidebarCollapsed();
    this._toggleSubmenuContentStyles();

    this._updateState();
  }

  toggleOnMobile() {
    this.contentTarget.classList.toggle('hidden');
    this.mobileIconTargets.forEach(icon => {
      icon.classList.toggle('hidden');
    });
  }

  get _contentContainer() {
    return document.getElementById('sidebar-content-container');
  }

  _toggleAvatarPosition() {
    this.userMenuTarget.classList.toggle('sm:items-center');
  }

  _toggleSidebarClasses() {
    this.element.classList.toggle('sidebar-collapsed');

    ['sm:w-[250px]', 'w-full'].forEach(style => {
      this.element.classList.toggle(style);
    });

    const isExpanded = this.element.classList.contains('sm:w-[250px]');
    const container = this._contentContainer;

    if (container) {
      container.classList.toggle('sidebar-content-expanded', isExpanded);
      container.classList.toggle('sidebar-content-collapsed', !isExpanded);
    }

    if (this.hasLogoIconTarget) {
      this.logoIconTarget.classList.toggle('sm:hidden', isExpanded);
      this.logoIconTarget.classList.toggle('sm:block', !isExpanded);
    }

    if (window.Turbolinks) {
      Turbolinks.clearCache();
    }
  }

  _toggleTopNavClasses() {
    ['sm:justify-between', 'sm:justify-center'].forEach(style => {
      this.topNavTarget.classList.toggle(style);
    });
  }

  _toggleLogoClasses() {
    this.logoTarget.classList.toggle('sm:hidden');
  }

  _toggleIconClasses() {
    if (this.iconExpandedTarget.classList.contains('sm:flex')) {
      this._hideIcon(this.iconExpandedTarget);
      this._showIcon(this.iconCollapsedTarget);
    } else {
      this._hideIcon(this.iconCollapsedTarget);
      this._showIcon(this.iconExpandedTarget);
    }
  }

  _hideIcon(iconTarget) {
    iconTarget.classList.remove('sm:flex');
    iconTarget.classList.add('sm:hidden');
  }

  _showIcon(iconTarget) {
    iconTarget.classList.remove('sm:hidden');
    iconTarget.classList.add('sm:flex');
  }

  _hideItemText() {
    this.menuItemTargets.forEach(item => {
      item.classList.toggle('sm:hidden');
    })
  }

  _toggleUserMenu() {
    this.userAvatarTargets.forEach(avatar => {
      avatar.classList.toggle('sm:hidden');
    });

    this._collapseUserMenuAccordion();
  }

  _collapseMenuGroupsIfSidebarCollapsed() {
    const isCollapsed = this.element.classList.contains('sidebar-collapsed');
    if (!isCollapsed) return;

    this._collapseAllMenuGroups();
  }

  _toggleSubmenuContentStyles() {
    const isCollapsed = this.element.classList.contains('sidebar-collapsed');

    this.submenuContentTargets.forEach(el => {
      el.classList.toggle('sidebar-submenu-collapsed', isCollapsed);
    });

    this.submenuIconTargets.forEach(icon => {
      icon.classList.toggle('sidebar-menu-collapsed', isCollapsed);
    });
  }

  _collapseOnOutsideClick(event) {
    if (this.element.contains(event.target)) return;

    this._collapseAllMenuGroups();
    this._collapseUserMenuAccordion();
  }

  _prepareForCache() {
    this.submenuContentTargets.forEach(el => {
      if (el === this._serverActiveGroup) return;

      this._hideMenuGroup(el);
    });
    this._hideUserMenu();
  }

  // Bypasses accordion controller's collapse() because it uses setTimeout(50ms),
  // which is too slow for Turbolinks cache/render hooks — the snapshot would be
  // taken before the groups are actually hidden, causing a visual flash.
  _hideAllMenuGroups() {
    this.submenuContentTargets.forEach(el => {
      this._hideMenuGroup(el);
    });
    this._hideUserMenu();
  }

  _hideMenuGroup(el) {
    if (!el.classList.contains('collapsed')) {
      el.classList.add('collapsed');
      el.style.height = '0';
      const icon = el.closest('[data-controller="components--accordion"]')
                      ?.querySelector('[data-components--accordion-target="icon"]');
      if (icon) icon.classList.add('collapsed');
    }
  }

  toggleUserMenu() {
    this.submenuContentTargets.forEach(el => this._hideMenuGroup(el));
    this._positionUserMenuContent();

    const accordionController = this.application.getControllerForElementAndIdentifier(
      this.userMenuTarget, 'components--accordion'
    );
    if (accordionController) accordionController.toggle();
  }

  _positionUserMenuContent() {
    if (!this.hasUserMenuTarget) return;
    const content = this.userMenuTarget.querySelector('.accordion-content');
    const toggler = this.userMenuTarget.querySelector('[data-action*="toggleUserMenu"]');
    if (!content || !toggler) return;

    // Only position when absolute (desktop). On mobile, content is in-flow.
    if (window.getComputedStyle(content).position !== 'absolute') {
      content.style.top = '';
      return;
    }

    const parent = content.offsetParent;
    if (!parent) return;

    const togglerRect = toggler.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const togglerBottom = togglerRect.bottom - parentRect.top;
    content.style.top = (togglerBottom - content.scrollHeight) + 'px';
  }

  _hideUserMenu() {
    if (!this.hasUserMenuTarget) return;
    const content = this.userMenuTarget.querySelector('.accordion-content');
    if (content && !content.classList.contains('collapsed')) {
      content.classList.add('collapsed');
      content.style.height = '0';
    }
  }

  _collapseAllMenuGroups() {
    this.submenuContentTargets.forEach(el => {
      const controller = this.application.getControllerForElementAndIdentifier(
        el.closest('[data-controller="components--accordion"]'),
        'components--accordion'
      );

      if (controller && !el.classList.contains('collapsed')) {
        controller.collapse();
      }
    });
  }

  _collapseUserMenuAccordion() {
    if (!this.hasUserMenuTarget) return;

    const accordionController = this.application.getControllerForElementAndIdentifier(
      this.userMenuTarget, 'components--accordion'
    );
    if (!accordionController) return;

    const content = this.userMenuTarget.querySelector('.accordion-content');
    if (content && !content.classList.contains('collapsed')) {
      accordionController.collapse();
    }
  }

  _updateState() {
    Rails.ajax({
      url: `${window.location.origin}/admin/sidebar`,
      type: "PATCH",
      dataType: "script",
      beforeSend: (xhr) => {
        this.ajaxRequest = xhr;
        return true;
      }
    });
  }
}
